import { useState } from 'react'
import { SelectPicker } from 'rsuite'
import { t } from 'i18next'
import ModalDialog from '@client/components/ModalDialog'
import { TRenameOffer } from './renameOffers'

//
// Asked after the code was saved, never before: the code is valid and belongs on
// disk whatever the answer here is. Nobody loses work by dismissing a question.
//
// One row per mapping rule, laid out as a list: old name on the left, the
// function it should point at from now on on the right.
//
// ⚠️ The field name under the old name is not decoration. One row per RULE means
// the same function can appear twice — it may fill two target fields — and
// without the field name those two rows are indistinguishable, while still
// needing an answer each. Seen in the check on 06.09., where fullName filled
// both "Name" and "Kunden-Nr.".
//

type LabelValueProps = { label: string; value: string }

type TProps = {
    offers: TRenameOffer[]
    /** Every function the code defines now — what a rule may be pointed at. */
    methods: LabelValueProps[]
    /** mapping row id -> the function it should point at from now on */
    onApply: (assignments: Record<string, string>) => void
    onCancel: () => void
}

const TableStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0 1.5em',
    alignItems: 'center',
    marginTop: '1em',
}
const HeadStyle = {
    fontWeight: 600,
    paddingBottom: '0.4em',
    borderBottom: '1px solid var(--rs-border-primary)',
}
const CellStyle = {
    padding: '0.55em 0',
    borderBottom: '1px solid var(--rs-border-secondary)',
}
const FieldStyle = {
    opacity: 0.6,
    fontSize: '0.85em',
}

export default function RenameFunctionsDialog({ offers, methods, onApply, onCancel }: TProps) {
    // Pre-filled with the proposal where there is one. An offer without a
    // proposal starts empty and stays empty unless somebody picks something —
    // deleting a function without replacing it is a normal thing to do.
    const [assignments, setAssignments] = useState<Record<string, string>>(() =>
        Object.fromEntries(offers.map((offer) => [offer.rowId, offer.suggested]))
    )

    const buttons = [
        { label: t('Apply assignments'), status: 'apply' },
        { label: t('Change nothing'), status: 'cancel' },
    ]

    const handleDialogButtons = (status: string) => {
        if (status === 'apply') onApply(assignments)
        else onCancel()
        return true
    }

    const pick = (rowId: string, value: string) => {
        setAssignments((current) => ({ ...current, [rowId]: value || '' }))
    }

    return (
        <ModalDialog
            title={t('Functions were renamed or removed')}
            buttons={buttons}
            onClick={handleDialogButtons}
            size="sm"
        >
            <p>
                {t(
                    'These fields use a function the code no longer defines. Until this is corrected they produce an error for every row instead of a value.'
                )}
            </p>

            <div style={TableStyle}>
                <div style={HeadStyle}>{t('old')}</div>
                <div style={HeadStyle}>{t('new')}</div>

                {offers.map((offer) => (
                    <div key={offer.rowId} style={{ display: 'contents' }}>
                        <div style={CellStyle}>
                            <div>{offer.oldName}</div>
                            <div style={FieldStyle}>{offer.targetLabel}</div>
                        </div>
                        <div style={CellStyle}>
                            <SelectPicker
                                data={methods}
                                value={assignments[offer.rowId] || null}
                                onChange={(value) => pick(offer.rowId, String(value ?? ''))}
                                placeholder={t('Leave unassigned')}
                                searchable={false}
                                block
                            />
                        </div>
                    </div>
                ))}
            </div>
        </ModalDialog>
    )
}
